import React, { useEffect, lazy, memo, createElement } from 'react';
import { LandingPage } from './pages/LandingPage';
const LCP_IMAGE = 'https://cdn.magicpatterns.com/uploads/3LNb7KBodWyM1Ekg1gLqBh/Texto-_2_.webp';
const GOOGLE_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=optional';
const PIXEL_ID = '1497337765421439';
/**
 * PERF: Initialize the `fbq` queue stub synchronously at module load (before
 * any user interaction can happen). This ensures CTA clicks that fire
 * `fbq('track', 'AddToCart', ...)` BEFORE the fbevents.js script finishes
 * lazy-loading still queue correctly and replay once the SDK is ready —
 * preventing silently-dropped conversion events on Meta Ads traffic.
 *
 * No network request happens here — only the in-memory queue is created.
 * The actual SDK script is still deferred to requestIdleCallback below.
 */
function ensureFbqStub() {
  const w = window as any;
  if (w.fbq) return;
  const n: any = w.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
}
if (typeof window !== 'undefined') {
  ensureFbqStub();
}
function loadMetaPixel() {
  const w = window as any;
  ensureFbqStub();
  // Guard: if the real SDK is already loaded, do nothing.
  if (w.fbq && (w.fbq as any).callMethod) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  w.fbq('init', PIXEL_ID);
  w.fbq('track', 'PageView');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
  document.body.appendChild(img);
}
export function App() {
  useEffect(() => {
    // PERF: Preconnect to critical origins (real TCP+TLS warmup).
    const preconnects = [{
      href: 'https://fonts.gstatic.com',
      crossOrigin: 'anonymous'
    }, {
      href: 'https://cdn.magicpatterns.com',
      crossOrigin: 'anonymous'
    }];
    preconnects.forEach(({
      href,
      crossOrigin
    }) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      if (crossOrigin) link.crossOrigin = crossOrigin;
      document.head.appendChild(link);
    });
    // PERF: DNS-prefetch for Meta Pixel origin — cheaper than a full preconnect
    // because we don't need TLS warmup before the idle-time script load. Saves
    // ~100-200ms on the first AddToCart trip without consuming a connection slot.
    const dnsPrefetches = ['https://connect.facebook.net', 'https://www.facebook.com'];
    dnsPrefetches.forEach(href => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = href;
      document.head.appendChild(link);
    });
    // PERF: Preload LCP image with high-priority fetch + correct as=image hint
    // so the browser starts the request in parallel with HTML parsing.
    const preload = document.createElement('link');
    preload.rel = 'preload';
    preload.as = 'image';
    preload.href = LCP_IMAGE;
    (preload as any).fetchPriority = 'high';
    document.head.appendChild(preload);
    // PERF: Load Google Fonts asynchronously (non-render-blocking via the
    // print->all media swap pattern). `display=optional` already prevents
    // FOIT, and our Inter Fallback @font-face with size-adjust prevents CLS.
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = GOOGLE_FONTS_URL;
    fontLink.media = 'print';
    fontLink.onload = function () {
      fontLink.media = 'all';
    };
    document.head.appendChild(fontLink);
    // PERF: Defer Meta Pixel SDK to idle time so it doesn't compete with the
    // LCP image or hydration. The synchronous fbq stub above ensures any
    // earlier CTA clicks still queue correctly.
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadMetaPixel, {
        timeout: 3500
      });
    } else {
      setTimeout(loadMetaPixel, 2000);
    }
  }, []);
  return <LandingPage data-id="element-0" />;
}